import { jsonLd } from "@/lib/seoHelpers";
import { Helmet } from "react-helmet-async";

/**
 * "Key takeaways" answer block.
 *
 * AI answer engines (ChatGPT Search, Perplexity, Google AI Overviews) strongly
 * prefer short, self-contained, extractable statements near the top of a page.
 * This derives 3–5 concise takeaways from the article's own section headings and
 * their opening sentence, renders them as a quotable list marked
 * `data-speakable`, and mirrors them as ItemList structured data.
 *
 * It renders nothing when the article already contains its own takeaways block
 * or when we can't extract enough clean material.
 */

const stripTags = (html: string) =>
  html
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#39;|&rsquo;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();

interface Section {
  heading: string;
  text: string;
}

/** Prose paragraphs only — tables, lists and headings flatten into gibberish. */
function paragraphText(chunk: string): string {
  const paras = [...chunk.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)].map((m) => stripTags(m[1]));
  if (paras.length > 0) return paras.join(" ");
  // Markdown: keep plain prose lines only
  return chunk
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !/^([|>#*\-+]|\d+[.)]|!\[|\[)/.test(l))
    .join(" ");
}

function extractSections(content: string): Section[] {
  const sections: Section[] = [];

  // HTML headings
  const htmlPattern = /<h2[^>]*>([\s\S]*?)<\/h2>([\s\S]*?)(?=<h2|$)/gi;
  let match: RegExpExecArray | null;
  while ((match = htmlPattern.exec(content)) !== null) {
    sections.push({ heading: stripTags(match[1]), text: paragraphText(match[2]) });
  }

  if (sections.length === 0) {
    // Markdown headings
    const mdPattern = /^##\s+([^\n]+)\n([\s\S]*?)(?=\n##\s|$)/gm;
    while ((match = mdPattern.exec(content)) !== null) {
      sections.push({ heading: stripTags(match[1]), text: paragraphText(match[2]) });
    }
  }

  return sections;
}

const SKIP_HEADING = /(faq|frequently asked|conclusion|final thoughts|related|about the author|disclosure|where to buy|references)/i;

function firstSentence(text: string): string {
  const sentences = text.split(/(?<=[.!?])\s+/);
  for (const raw of sentences) {
    const s = raw.trim();
    if (s.length < 60 || s.length > 220) continue;
    if (!/[.!?]$/.test(s)) continue;
    if (s.includes("|")) continue;
    if (/^(here|below|in this|read on|we ll|let s)/i.test(s)) continue;
    return s;
  }
  return "";
}


export function buildTakeaways(content: string | null): string[] {
  if (!content) return [];
  if (/key\s*takeaways/i.test(content)) return [];
  const plain = stripTags(content);
  if (plain.split(" ").length < 500) return [];

  const takeaways: string[] = [];
  for (const section of extractSections(content)) {
    if (!section.heading || SKIP_HEADING.test(section.heading)) continue;
    const sentence = firstSentence(section.text);
    if (!sentence) continue;
    if (takeaways.some((t) => t === sentence)) continue;
    takeaways.push(sentence);
    if (takeaways.length === 5) break;
  }

  return takeaways.length >= 3 ? takeaways : [];
}

interface KeyTakeawaysProps {
  content: string | null;
  title: string;
}

export const KeyTakeaways = ({ content, title }: KeyTakeawaysProps) => {
  const takeaways = buildTakeaways(content);
  if (takeaways.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Key takeaways: ${title}`,
    itemListElement: takeaways.map((text, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: text,
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{jsonLd(schema)}</script>
      </Helmet>
      <section
        aria-labelledby="key-takeaways-heading"
        data-speakable="true"
        className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-5 md:p-6"
      >
        <h2
          id="key-takeaways-heading"
          className="text-lg font-bold text-foreground mb-3"
        >
          Key takeaways
        </h2>
        <ul className="list-disc pl-5 space-y-2 text-foreground/90 leading-relaxed break-words">
          {takeaways.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </section>
    </>
  );
};

export default KeyTakeaways;
